import { createBrowserRouter, Navigate } from 'react-router-dom'
import { AuthGuard } from '~/components/layout/auth-guard'
import { ErrorBoundary } from '~/components/shared/error-boundary'
import { LoginPage } from '~/pages/login-page'
import { SignupPage } from '~/pages/signup-page'
import { HomePage } from '~/pages/home-page'
import { CertificationListPage } from '~/pages/certification-list-page'
import { CertificationDetailPage } from '~/pages/certification-detail-page'
import { ReportPrintPage } from '~/pages/report-print-page'
import { ConsultationChatPage } from '~/pages/consultation-chat-page'
import { ConsultationHistoryPage } from '~/pages/consultation-history-page'
import { MaintenancePage } from '~/pages/maintenance-page'
import { MaintenanceReportPage } from '~/pages/maintenance-report-page'
import { ExplorePage } from '~/pages/explore-page'
import { MonitoringPage } from '~/pages/monitoring-page'
import { MonitoringDashboardPage } from '~/pages/monitoring-dashboard-page'
import { SessionPage } from '~/pages/session-page'
import type { ReactElement } from 'react'

/** 인증이 필요한 라우트를 가드 + 에러 경계로 감싼다. */
function protect(element: ReactElement): ReactElement {
    return (
        <AuthGuard>
            <ErrorBoundary>{element}</ErrorBoundary>
        </AuthGuard>
    )
}

// SPA 모드(ssr:false). DESIGN.md §4.1 화면 구조를 그대로 따른다.
export const router = createBrowserRouter([
    // knoka SSO 콜백은 서버사이드(GET /api/auth/callback/)가 처리하고 앱 루트로 돌려보낸다.
    { path: '/login', element: <LoginPage /> },
    { path: '/signup', element: <SignupPage /> },

    { path: '/', element: protect(<HomePage />) },

    // 나의 HACCP 인증 AI (평가)
    { path: '/certification', element: protect(<CertificationListPage />) },
    {
        path: '/certification/:id',
        element: protect(<CertificationDetailPage />),
    },
    // 인쇄 전용 보고서(앱 크롬 없이 A4 문서 → 브라우저 PDF 저장)
    {
        path: '/certification/:id/report',
        element: protect(<ReportPrintPage />),
    },

    // HACCP AI 상담챗봇
    {
        path: '/consultation',
        element: <Navigate to='/consultation/chat' replace />,
    },
    { path: '/consultation/chat', element: protect(<ConsultationChatPage />) },
    {
        path: '/consultation/history',
        element: protect(<ConsultationHistoryPage />),
    },

    // HACCP 유지하기 AI (유지)
    { path: '/maintenance', element: protect(<MaintenancePage />) },
    // 인쇄 전용 변경 영향 분석 보고서(앱 크롬 없이 standalone, localStorage 핸드오프)
    { path: '/maintenance/report', element: protect(<MaintenanceReportPage />) },

    // HACCP 체크리스트 찾아보기
    { path: '/explore', element: protect(<ExplorePage />) },

    // 모니터링(준비 중) — 헤더 탭은 배경색과 동일 색으로 숨김. 공개 전이라 사용자 비노출.
    { path: '/monitoring', element: protect(<MonitoringPage />) },
    { path: '/monitoring/:id', element: protect(<MonitoringDashboardPage />) },
    {
        path: '/monitoring/:id/:section',
        element: protect(<MonitoringDashboardPage />),
    },

    // 지난 대화 보기
    { path: '/sessions/:id', element: protect(<SessionPage />) },

    // 알 수 없는 경로는 홈으로.
    { path: '*', element: <Navigate to='/' replace /> },
])
